import { ChangeDetectionStrategy, Component, TrackByFunction, Inject, OnDestroy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { UrlService } from '../singleton-services/url.service'
import { BehaviorSubject, combineLatest, iif, of, Scheduler, Subject } from 'rxjs'
import { FloApiResponse, Meta } from '../shared/models/api-response.model'
import { IVideo, VideoService } from '../shared/services/video.service'
import { NodeService } from '../shared/services/node.service'
import { filter, switchMap, map, scan, shareReplay, takeUntil, tap } from 'rxjs/operators'
import { SegmentService } from '../shared/analytics/services/segment.service'
import { SegmentTagMap } from '../shared/analytics/models/segment-tag-map.model'
import { RXJS_SCHEDULER } from '../app.config'
import { WINDOW } from '../app.injections'

@Component({
  selector: 'flo-films',
  templateUrl: './films.component.html',
  styleUrls: ['./films.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FloFilmsComponent implements OnDestroy {
  constructor(
    private ar: ActivatedRoute,
    private url: UrlService,
    private router: Router,
    private videoService: VideoService,
    private nodeService: NodeService,
    @Inject(WINDOW) private readonly window: Window,
    @Inject(RXJS_SCHEDULER) private scheduler: Scheduler,
    private segmentService: SegmentService
  ) {}
  private readonly ngOnDestroy$ = new Subject<void>()
  public pageSubject$ = new BehaviorSubject<number>(1)
  private readonly searchTermSubject = new Subject<string | null>()

  public handleSearch(event: { searchTerm: string | null }) {
    this.searchTermSubject.next(event.searchTerm)
  }

  // To ensure we always call a new query/deleting of the query with page 1
  // we compare the last query with the current and if its new we fire with page 1
  params$ = combineLatest([this.pageSubject$, this.ar.queryParams]).pipe(
    map(([pageNumber, params]) => ({ page: pageNumber, ...params })),
    scan((acc: any, curr: any) => (acc.q !== curr.q ? { ...curr, page: 1 } : curr)),
    map(params =>
      Object.keys(params).reduce(
        (acc, c) => ({ ...acc, [c]: this.url.decode(params[c]) }),
        {} as { [key: string]: string }
      )
    ),
    shareReplay(1)
  )

  // scanning collection of videos. If we are on page 1 then we only want the current videos,
  // if the pages are the same then want to keep the accumulated collection and pass the current params,
  // otherwise we want to send the accumulated list concatted with the current videos

  videosResponse$ = this.params$.pipe(
    switchMap(params => {
      return combineLatest([
        this.videoService.queryVideos(params, 'FloFilm,Documentary'),
        iif(
          () => (params.playing ? true : false),
          this.videoService.getVideoAssociations(params.playing as any),
          of([])
        )
      ]).pipe(
        map(([videos, associatedVideos]) => {
          return {
            params,
            response: videos,
            associatedVideos
          }
        })
      )
    }),
    scan((acc, curr) => {
      // There were cases where a video was in an associated list and when selected
      // it returned with no associated videos for itself. even though it was just in an associated list
      // this prevents that behavior by checking if it was in the previous associated list and
      // if so return that same previous list. The unwanted behavior comes from the CMS not setting
      // the primary association
      const isVideoAssociated = acc.associatedVideos.associated_videos
        ? acc.associatedVideos.associated_videos.find((a: IVideo) => a && a.id === +(curr.params.playing || 0))
        : false

      const currentAssociatedVideos = isVideoAssociated
        ? acc.associatedVideos.associated_videos
        : curr.associatedVideos.associated_videos

      if (curr.params.page === '1') {
        return {
          ...curr,
          associatedVideos: {
            video: curr.associatedVideos.video,
            associated_videos: currentAssociatedVideos
          }
        }
      } else if (acc.params.page === curr.params.page) {
        return {
          ...acc,
          params: curr.params,
          associatedVideos: {
            video: curr.associatedVideos.video,
            associated_videos: currentAssociatedVideos
          }
        }
      }
      return {
        params: curr.params,
        response: {
          meta: curr.response.meta,
          data: [...acc.response.data, ...curr.response.data]
        },
        associatedVideos: {
          video: curr.associatedVideos.video,
          associated_videos: currentAssociatedVideos
        }
      }
    }),
    shareReplay(1)
  )

  view$ = this.videosResponse$.pipe(
    map(videosResponse => {
      const params = videosResponse.params
      const extractedVid = this.extractPlayingVideoFromMeta(videosResponse.response) as any
      const meta = videosResponse.response.meta
      const videos = videosResponse.response.data || []
      const associatedVideos = videosResponse.associatedVideos.associated_videos || []
      const seriesTitle =
        videosResponse.associatedVideos.video &&
        videosResponse.associatedVideos.video.node.primary_event_or_series_association &&
        videosResponse.associatedVideos.video.node.primary_event_or_series_association.title
      const combinedVideos = associatedVideos.concat(videos) as ReadonlyArray<IVideo>
      const canLoadMore = meta && meta.page && meta.pages && meta.page < meta.pages
      const playingVideo = combinedVideos.find(a => a && a.id === +(params.playing || 0)) || extractedVid

      const nextVideoIndex = combinedVideos.findIndex(a => a && a.id === +(params.playing || 0))

      const nextVideoId =
        (combinedVideos[nextVideoIndex] &&
          combinedVideos[nextVideoIndex + 1] &&
          combinedVideos[nextVideoIndex + 1].id) ||
        (combinedVideos[0] && combinedVideos[0].id)

      const searchTerm = params.q || ''

      const selectedVideo = playingVideo && {
        video: playingVideo,
        date: playingVideo.publish_start_date,
        description: playingVideo.description || playingVideo.description_plain,
        isRich: playingVideo.description ? true : false
      }

      const searchTotal = searchTerm ? `${videos.length} Videos` : ''
      return {
        searchTotal,
        selectedVideo,
        playingVideo,
        meta,
        params,
        videos,
        queryParamsFn: (video: IVideo) => ({ playing: video.id }),
        searchTerm,
        sort: params.sort || 'recent',
        totalTitle: 'Video',
        totalTitlePlural: 'Videos',
        canLoadMore,
        nextVideoId,
        notFoundWarning: meta.error ? 'An error occurred, please try again later' : undefined,
        associatedVideos,
        seriesTitle: `More ${seriesTitle}`,
        nextVideoIndex,
        analyticsProps: { search_term: searchTerm }
      }
    }),
    tap(view => {
      this.segmentService.page(undefined, SegmentTagMap.FLOFILMS.name, {}, view.playingVideo)
      this.nodeService.applyNodeMetaToPage(view.playingVideo)
    })
  )

  public searchParam$ = this.ar.queryParams.pipe(
    filter(param => param.q),
    map(param => param.q || ''),
    shareReplay(1)
  )

  searchVideoOnQueryInput$ = this.searchTermSubject.pipe(takeUntil(this.ngOnDestroy$)).subscribe(searchTerm => {
    const { playing, ...queryParams } = this.ar.snapshot.queryParams
    this.router.navigate([], {
      queryParams: { ...queryParams, ...{ q: searchTerm ?? null } },
      queryParamsHandling: 'merge'
    })
  })

  extractPlayingVideoFromMeta(response: FloApiResponse<ReadonlyArray<IVideo>, Meta & { extra?: ReadonlyArray<any> }>) {
    return (
      response.meta &&
      response.meta.extra &&
      response.meta.extra.reduce(
        (acc, cur) =>
          Object.keys(cur)
            .filter(a => a === 'include_node')
            .map(a => (cur as { [key: string]: any })[a] as string)
            .pop(),
        {}
      )
    )
  }

  onPlayed(id: any) {
    if (id) {
      this.onVideoSelectionChange({ id } as any)
    }
  }

  onVideoSelectionChange(e: IVideo) {
    this.router
      .navigate([], {
        queryParams: { playing: e.id },
        queryParamsHandling: 'merge'
      })
      .then(
        () =>
          this.window &&
          this.window.scroll &&
          this.window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth'
          })
      )
  }

  onPage(page: number) {
    this.pageSubject$.next(page)
  }

  trackByVideo: TrackByFunction<any> = (index: number, item: any) => {
    return item.id
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
